import { PERMISSIONS } from '../constants/permissions';
import type { OrganizationModuleCapabilities } from '../types/organization';
import { hasPermission } from '../utils/permissions';

/**
 * Organization master-data capabilities from mounted arkhrmsapi routes.
 *
 * Mounted:
 * - GET/PUT /organization
 * - GET/POST /organization/locations, PUT /organization/locations/:id
 * - GET/POST /organization/departments, PUT /organization/departments/:id
 * - GET/POST /organization/designations, PUT /organization/designations/:id
 * - GET/POST /organization/teams, PUT /organization/teams/:id
 * - GET/POST /organization/teams/:id/members, DELETE .../members/:employeeId
 *
 * Not mounted:
 * - GET by id for locations / departments / designations / teams
 * - DELETE for any organization master entity
 * - Country / timezone / level / grade lookup catalogs
 *
 * Installed permissions are SETTINGS_VIEW and SETTINGS_UPDATE only.
 */
export const ORGANIZATION_ROUTE_CAPABILITIES = {
  organizationGet: true,
  organizationUpdate: true,
  locationList: true,
  locationCreate: true,
  locationUpdate: true,
  locationDetails: false,
  departmentList: true,
  departmentCreate: true,
  departmentUpdate: true,
  departmentDetails: false,
  designationList: true,
  designationCreate: true,
  designationUpdate: true,
  designationDetails: false,
  teamList: true,
  teamCreate: true,
  teamUpdate: true,
  teamDetails: true,
  delete: false,
} as const;

export function getOrganizationModuleCapabilities(
  permissions: readonly string[],
): OrganizationModuleCapabilities {
  const canView = hasPermission(permissions, PERMISSIONS.SETTINGS_VIEW);
  const canUpdate = hasPermission(permissions, PERMISSIONS.SETTINGS_UPDATE);

  return {
    canViewOrganization: canView && ORGANIZATION_ROUTE_CAPABILITIES.organizationGet,
    canUpdateOrganization:
      canUpdate && ORGANIZATION_ROUTE_CAPABILITIES.organizationUpdate,

    canViewLocations: canView && ORGANIZATION_ROUTE_CAPABILITIES.locationList,
    canCreateLocation: canUpdate && ORGANIZATION_ROUTE_CAPABILITIES.locationCreate,
    canUpdateLocation: canUpdate && ORGANIZATION_ROUTE_CAPABILITIES.locationUpdate,

    canViewDepartments: canView && ORGANIZATION_ROUTE_CAPABILITIES.departmentList,
    canCreateDepartment:
      canUpdate && ORGANIZATION_ROUTE_CAPABILITIES.departmentCreate,
    canUpdateDepartment:
      canUpdate && ORGANIZATION_ROUTE_CAPABILITIES.departmentUpdate,

    canViewDesignations: canView && ORGANIZATION_ROUTE_CAPABILITIES.designationList,
    canCreateDesignation:
      canUpdate && ORGANIZATION_ROUTE_CAPABILITIES.designationCreate,
    canUpdateDesignation:
      canUpdate && ORGANIZATION_ROUTE_CAPABILITIES.designationUpdate,

    canViewTeams: canView && ORGANIZATION_ROUTE_CAPABILITIES.teamList,
    canCreateTeam: canUpdate && ORGANIZATION_ROUTE_CAPABILITIES.teamCreate,
    canUpdateTeam: canUpdate && ORGANIZATION_ROUTE_CAPABILITIES.teamUpdate,
  };
}
